import isEmpty from 'lodash/isEmpty'
import React from 'react'
import { Col, Nav, Row, Stack, Tab, Table } from 'react-bootstrap'
import type { Task, RouteDef } from '@ztechapps/common.contract'
import { TaskStatusEnum } from '@ztechapps/common.contract'
import { useDocument } from '@ztechapps/common.hook'
import { useInject, WebuiIocKey } from '@ztechapps/webui.contract'
import {
  HorizontalStack,
  Icon,
  IconValueBlock,
  LoadingPlaceholder,
  Scrollable,
  ShowIf,
  TooltipPopOver,
} from '@ztechapps/webui.util'
import { CopyToClipboardButton, BusyButtonLink } from '@ztechapps/webui.button'
import { Link } from 'react-router-dom'
import { LocalDateTime } from '@ztechapps/webui.date'
import { RichTextDisplay, RichTextInlineInput } from '@ztechapps/webui.input'
import { useTaskTooltipPopOverContext } from './Context'
import { TaskTooltipPopOverInlineInput } from './TaskTooltipPopOverInlineInput'
import { taskTooltipQueries } from './graphql'
import { TaskStatusDropdown } from '../TaskStatus/TaskStatusDropdown'
import { TaskStatusBadge } from '../TaskStatus/TaskStatusBadge'
import { TaskMembersInlineInput } from '../task-inlineinput/TaskMembersInlineInput'
import { TaskStartDateInlineInput, TaskEndDateInlineInput } from '../task-inlineinput/TaskDateInlineInput'
import { TaskTimeSpentInlineInput } from '../task-inlineinput/TaskTimeSpentInlineInput'
import { TaskPercentCompleteInlineInput } from '../task-inlineinput/TaskPercentCompleteInlineInput'
import { TaskTagsInlineInput } from '../task-inlineinput/TaskTagsInlineInput'
import { useDeleteTaskWithConfirm } from '../shared/useDeleteTaskWithConfirm'

const MAX_VISIBLE_SUBTASKS = 3

export const Body = () => {
  const { taskId, canEdit } = useTaskTooltipPopOverContext()
  const taskRoute = useInject<RouteDef>(WebuiIocKey.routes.taskItem)
  const projectRoute = useInject<RouteDef>(WebuiIocKey.routes.projectItem)
  const workOrderRoute = useInject<RouteDef>(WebuiIocKey.routes.workOrderItem)
  const { onDelete, deleting } = useDeleteTaskWithConfirm()

  const taskDocument = useDocument<Task>({
    query: taskTooltipQueries.singleConfig.query,
    _id: taskId,
  })

  const loading = taskDocument.loading
  const task = taskDocument?.document
  const subTasks = task?.subTasks ?? []
  const completedSubTasks = subTasks.filter((s) => s?.status === TaskStatusEnum.Complete).length
  const hasSubTasks = !isEmpty(subTasks)
  const visibleSubTasks = subTasks.slice(0, MAX_VISIBLE_SUBTASKS)
  const remainingCount = subTasks.length - MAX_VISIBLE_SUBTASKS

  return (
    <div style={{ width: '30vw', maxHeight: '80vh' }}>
      <Stack gap={1}>
        {/* Header: Name + Code + Actions */}
        <LoadingPlaceholder loading={loading}>
          <HorizontalStack gap={1} className="justify-content-between">
            <div className="flex-fill">
              <TaskTooltipPopOverInlineInput
                name="name"
                taskDocument={taskDocument}
                displayContent={(value) => <h5 className="m-0">{value}</h5>}
              />
            </div>
            <HorizontalStack gap={2} className="flex-shrink-0">
              <span className="text-secondary">{task?.code}</span>
              <ShowIf condition={!!task?.code}>
                <CopyToClipboardButton textToCopy={task?.code} />
              </ShowIf>
              <ShowIf condition={canEdit}>
                <TooltipPopOver tooltipBody="Delete this task">
                  <button
                    type="button"
                    aria-label="Delete this task"
                    className="btn btn-link text-danger p-0 border-0"
                    disabled={deleting}
                    onClick={() => onDelete(task?._id)}
                  >
                    <Icon name="trash" aria-hidden="true" />
                  </button>
                </TooltipPopOver>
              </ShowIf>
              <ShowIf condition={!!taskRoute}>
                <TooltipPopOver tooltipBody="Open in new window">
                  <Link aria-label="Open in new window" to={taskRoute?.altPath?.({ _id: task?._id })} target="_blank">
                    <Icon name="external-link" aria-hidden="true" />
                  </Link>
                </TooltipPopOver>
              </ShowIf>
            </HorizontalStack>
          </HorizontalStack>
        </LoadingPlaceholder>

        {/* Status + Members */}
        <LoadingPlaceholder loading={loading}>
          <HorizontalStack gap={2} className="justify-content-between">
            <TaskStatusDropdown value={task?.status} taskId={task?._id} disabled={!canEdit} />
            <TaskTooltipPopOverInlineInput
              name="members"
              parentInputComponent={TaskMembersInlineInput}
              taskDocument={taskDocument}
            />
          </HorizontalStack>
        </LoadingPlaceholder>

        {/* Project + Production links */}
        <ShowIf condition={!!task?.project?._id || !!task?.workOrder?._id}>
          <HorizontalStack gap={3}>
            <ShowIf condition={!!task?.project?._id}>
              <IconValueBlock
                icon="diagram-project"
                value={<a href={projectRoute.altPath?.({ _id: task?.project?._id })}>{task?.project?.code}</a>}
                help="Project"
              />
            </ShowIf>
            <ShowIf condition={!!task?.workOrder?._id}>
              <IconValueBlock
                icon="industry"
                value={<a href={workOrderRoute.altPath?.({ _id: task?.workOrder?._id })}>{task?.workOrder?.code}</a>}
                help="Production"
              />
            </ShowIf>
          </HorizontalStack>
        </ShowIf>

        <hr className="my-1" />

        {/* Dates */}
        <LoadingPlaceholder loading={loading}>
          <Row>
            <Col>
              <TaskTooltipPopOverInlineInput
                name="startDate"
                parentInputComponent={TaskStartDateInlineInput}
                taskDocument={taskDocument}
                displayContent={(value) => (
                  <IconValueBlock icon="calendar" help="Start Date">
                    <ShowIf condition={!!value}>
                      <LocalDateTime value={value} />
                    </ShowIf>
                    <ShowIf condition={!value}>---</ShowIf>
                  </IconValueBlock>
                )}
              />
            </Col>
            <Col>
              <TaskTooltipPopOverInlineInput
                name="endDate"
                parentInputComponent={TaskEndDateInlineInput}
                taskDocument={taskDocument}
                displayContent={(value) => (
                  <IconValueBlock icon="calendar-check" help="Due Date">
                    <ShowIf condition={!!value}>
                      <LocalDateTime value={value} />
                    </ShowIf>
                    <ShowIf condition={!value}>---</ShowIf>
                  </IconValueBlock>
                )}
              />
            </Col>
          </Row>
        </LoadingPlaceholder>

        {/* Time + Progress */}
        <LoadingPlaceholder loading={loading}>
          <Row>
            <Col>
              <TaskTooltipPopOverInlineInput
                name="timeSpent"
                parentInputComponent={TaskTimeSpentInlineInput}
                taskDocument={taskDocument}
              />
            </Col>
            <Col>
              <TaskTooltipPopOverInlineInput
                name="percentageComplete"
                parentInputComponent={TaskPercentCompleteInlineInput}
                taskDocument={taskDocument}
              />
            </Col>
          </Row>
        </LoadingPlaceholder>

        {/* Tags */}
        <LoadingPlaceholder loading={loading}>
          <TaskTooltipPopOverInlineInput
            name="tags"
            parentInputComponent={TaskTagsInlineInput}
            taskDocument={taskDocument}
          />
        </LoadingPlaceholder>

        {/* Tabs: Description + Sub Tasks */}
        <LoadingPlaceholder loading={loading}>
          <Tab.Container defaultActiveKey="description">
            <Nav variant="underline" className="mb-2 bg-body">
              <Nav.Item>
                <Nav.Link eventKey="description">Description</Nav.Link>
              </Nav.Item>
              <ShowIf condition={hasSubTasks}>
                <Nav.Item>
                  <Nav.Link eventKey="subtasks">
                    Sub Tasks ({completedSubTasks}/{subTasks.length})
                  </Nav.Link>
                </Nav.Item>
              </ShowIf>
            </Nav>

            <Scrollable height="30vh">
              <Tab.Content>
                <Tab.Pane eventKey="description">
                  <TaskTooltipPopOverInlineInput
                    name="description"
                    taskDocument={taskDocument}
                    parentInputComponent={RichTextInlineInput}
                    hoverToEdit={false}
                    inputProps={{ height: '25vh', variant: 'minimal' }}
                    emptyContent={(onClick) => (
                      <div>
                        <ShowIf condition={canEdit}>
                          <BusyButtonLink text="Add Description" onClick={onClick} icon="edit" />
                        </ShowIf>
                      </div>
                    )}
                    displayContent={(value, onClick) => (
                      <Stack gap={1}>
                        <ShowIf condition={canEdit}>
                          <div>
                            <BusyButtonLink text="Edit" onClick={onClick} icon="edit" bsSize="sm" />
                          </div>
                        </ShowIf>
                        {/* trustedSource: task descriptions are authored by staff via the same editor, not attacker-reachable */}
                        <RichTextDisplay contentHtml={value} trustedSource />
                      </Stack>
                    )}
                  />
                </Tab.Pane>

                <ShowIf condition={hasSubTasks}>
                  <Tab.Pane eventKey="subtasks">
                    <Table size="sm" hover className="mb-0">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleSubTasks.map((subTask) => (
                          <tr key={subTask._id}>
                            <td className="text-truncate" style={{ maxWidth: '200px' }}>
                              {subTask.name}
                            </td>
                            <td>
                              <TaskStatusBadge status={subTask.status} />
                            </td>
                          </tr>
                        ))}
                        <ShowIf condition={remainingCount > 0}>
                          <tr>
                            <td colSpan={2} className="text-secondary fst-italic">
                              +{remainingCount} more...
                            </td>
                          </tr>
                        </ShowIf>
                      </tbody>
                    </Table>
                  </Tab.Pane>
                </ShowIf>
              </Tab.Content>
            </Scrollable>
          </Tab.Container>
        </LoadingPlaceholder>
      </Stack>
    </div>
  )
}
